import { useContext, useEffect, useState } from "react";
import { Routes as ReactRouterRoutes, Route } from "react-router-dom";
import { CustomerContext } from "./providers";
import { useGlobalAction } from "@gadgetinc/react";
import { api } from "./api";
import { jwtDecode } from "jwt-decode";

/**
 * File-based routing.
 * @desc File-based routing that uses React Router under the hood.
 * To create a new route create a new .jsx file in `/pages` with a default export.
 *
 * Some examples:
 * * `/pages/index.jsx` matches `/`
 * * `/pages/blog/[id].jsx` matches `/blog/123`
 * * `/pages/[...catchAll].jsx` matches any URL not explicitly matched
 *
 * @param {object} pages value of import.meta.globEager(). See https://vitejs.dev/guide/features.html#glob-import
 *
 * @return {Routes} `<Routes/>` from React Router, with a `<Route/>` for each file in `pages`
 */
export default function ExternalRoutes({ pages }) {
  const routes = useRoutes(pages);
  const routeComponents = routes.map(({ path, component: Component }) => (
    <Route key={path} path={path} element={<Component />} />
  ));

  const NotFound = routes.find(({ path }) => path === "/notFound").component;

  const { user, customer, integrations } = useContext(CustomerContext);

  const [{ data, error, fetching }, getAlcmeonInMessageJWT] = useGlobalAction(
    api.getAlcmeonInMessageJWT
  );

  useEffect(() => {
    var customerIntegrations = "";
    for (const [key, value] of Object.entries(integrations)) {
      if (value) {
        customerIntegrations += key + ",";
      }
    }
    if (customerIntegrations !== "") {
      customerIntegrations = customerIntegrations.slice(0, -1);
    }
    getAlcmeonInMessageJWT({
      data: {
        user_email: user.email,
        user_firstname: user.firstName,
        user_lastname: user.lastName,
        customer_name: customer.name,
        customer_id: customer.id,
        customer_link:
          "https://app.getsimio.com/dashboard?invitation_code=" +
          customer.codeForLogin,
        customer_integrations: customerIntegrations,
      },
    });
  }, []);

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // Initial check

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [window.innerWidth]);

  const AlcmeonScript = () => {
    const langCode = "fr";
    const pageId =
      window.gadgetConfig.environment === "production" ? "p_143" : "p_128";
    const translations = {
      title: "SUPPORT SIMIO",
      subtitle: "Bienvenue !",
      //helperMessage1: "Bonjour 👋<br/>Vous avez une question ?",
      //helperMessage2: "Demandez ce que vous voulez 👇",
      helperMessage3:
        "<strong>Vous avez une conversation en cours</strong>, et nous sommes impatients d'en savoir plus 😊",
      helperMessage4: "Continuez ici 👇",
      send_your_email: "Type your email here",
      send_your_phone: "Type your phone here",
      send_a_message: "Écrire votre message ici",
      select_an_option: "Sélectionner une option ci-dessus",
    };


    

    useEffect(() => {
      // Check if the script is already present in the DOM
      if (data && !document.getElementById("alcmeon-inmessage")) {
        // Configure the Alcmeon script

        window.alcmeonInMessageConfig = {
          langCode: langCode,
          pageId: pageId,
          translations: translations,
          jwt: data,
        };

        // Load the script dynamically
        const script = document.createElement("script");
        script.id = "alcmeon-inmessage";
        script.src =
          "https://inmessage.alcmeon.com/prod/client/latest/mbr-v2.js";
        script.async = true;
        document.body.appendChild(script);

        // Clean up the script when the component unmounts
        return () => {
          // const script = document.getElementById("alcmeon-inmessage");
          // if (script) {
          //   document.body.removeChild(script);
          // }
        };
      }
    }, [data]);

    return null; // This component doesn't render anything
  };

  return (
    <>
      <ReactRouterRoutes>
        {routeComponents}
        <Route path="*" element={<NotFound />} />
      </ReactRouterRoutes>
      {!isMobile && (

      <AlcmeonScript />
      )}
    </>
  );
}

function useRoutes(pages) {
  const routes = Object.keys(pages)
    .map((key) => {
      let path = key
        .replace("./externalPages", "")
        .replace(/\.(t|j)sx?$/, "")
        /**
         * Replace /index with /
         */
        .replace(/\/index$/i, "/")
        /**
         * Only lowercase the first letter. This allows the developer to use camelCase
         * dynamic paths while ensuring their standard routes are normalized to lowercase.
         */
        .replace(/\b[A-Z]/, (firstLetter) => firstLetter.toLowerCase())
        /**
         * Convert /[handle].jsx and /[...handle].jsx to /:handle.jsx for react-router-dom
         */
        .replace(/\[(?:[.]{3})?(\w+?)\]/g, (_match, param) => `:${param}`);

      if (path.endsWith("/") && path !== "/") {
        path = path.substring(0, path.length - 1);
      }

      if (!pages[key].default) {
        console.warn(`${key} doesn't export a default React component`);
      }

      return {
        path,
        component: pages[key].default,
      };
    })
    .filter((route) => route.component);

  return routes;
}
